import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { ReactComponent as EnterIcon } from "../icons/Enter.svg";
import { ReactComponent as ShowIcon } from "../icons/Show.svg";
import styles from "./RoomEntryModal.scss";
import { useCssBreakpoints } from "react-use-css-breakpoints";
import { FormattedMessage } from "react-intl";
import { cleanDescription, getArtistName } from "../../utils/highlightedRooms";
import { calculateTimeLeft } from "./useCountdownTimer";
import CountdownDisplay from "./CountdownDisplay";
import { useEvent } from "./EventContextProvider";
import { getRoomPresentation, getStartTime } from "../../utils/events-utils";
import configs from "../../utils/configs";
import {
  Subtitle1,
  makeStyles,
  Button,
  Dialog,
  mergeClasses,
  OverlayDrawer,
  DrawerHeader,
  DrawerHeaderTitle,
  DrawerBody,
  DrawerFooter,
  Text
} from "@fluentui/react-components";
import { Dismiss20Regular, Settings20Filled, Warning20Filled } from "@fluentui/react-icons";
import { ReactComponent as EnterOnAR } from "../icons/EnterOnAR.svg";
import { ReactComponent as EnterOnVR } from "../icons/EnterOnVR.svg";
import configManager from "../../singleton/ConfigManager";
import useBreakpoints from "../hooks/useBreakpoints";
import { useSettingsContext } from "./SettingsContext";
import { fetchAvatar } from "../../utils/avatar-utils";

const useStyles = makeStyles({
  button: {
    width: "100%"
  },
  dialogContent: {
    display: "flex",
    flexDirection: "column",
    gap: "8px"
  },
  titleContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "start",
    gap: 0,
    marginBottom: "4px"
  },
  closeButton: {
    marginLeft: "auto"
  }
});
export function RoomEntryModal({
  roomId,
  roomName,
  showJoinRoom,
  onJoinRoom,
  onJoinAR,
  showEnterOnDevice,
  onEnterOnDevice,
  showSpectate,
  onSpectate,
  roomUserData,
  canEnter
}) {
  const breakpoint = useBreakpoints();

  const { eventData, error: eventError, slug } = useEvent();
  const [isReady, setIsReady] = useState(false);
  const [startTime, setStartTime] = useState(null);
  const [timeLeft, setTimeLeft] = useState(null);

  const { onOpenChange: onOpenSettingsChange, showVerseSettings } = useSettingsContext();

  const onVerseSettings = () => {
    onOpenSettingsChange(true);
  };

  const classes = useStyles();

  const onBack = () => {
    APP.scene.emit("leave_room_requested");
  };

  const fetchAndSetAvatar = async () => {
    const profile = window?.APP?.store?.state?.profile;
    if (!profile.thumbnail) {
      const avatar = await fetchAvatar(profile?.avatarId);

      if (avatar?.files?.thumbnail) {
        window.APP.store.update({
          profile: { ...profile, thumbnail: avatar.files.thumbnail }
        });
      }
    }
  };

  useEffect(() => {
    fetchAndSetAvatar();
  }, []);

  useEffect(() => {
    if (eventData) {
      window.APP.eventData = eventData;

      const isLobby = eventData.lobby?.name === roomName;

      const presentation = getRoomPresentation(eventData, roomId, roomName);
      const roomStartTime = getStartTime(presentation || eventData, isLobby);

      const hasStartTimeNotPassed = roomStartTime && roomStartTime > Date.now();

      if (hasStartTimeNotPassed) {
        setStartTime(roomStartTime);
        setTimeLeft(calculateTimeLeft(roomStartTime));
      }

      setIsReady(true);
    }

    if (!slug || eventError) {
      const roomStartTime = roomUserData?.start_time ? new Date(roomUserData.start_time).getTime() : null;
      setStartTime(roomStartTime);
      setTimeLeft(calculateTimeLeft(roomStartTime));
      setIsReady(true);
    }
  }, [slug, eventError, eventData, roomUserData?.start_time, roomId, roomName]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (timeLeft) {
        setTimeLeft(calculateTimeLeft(startTime));
      } else {
        clearTimeout(timer);
      }
    }, 1000);

    return () => clearTimeout(timer);
  });

  const [open, setIsOpen] = useState(true);

  const callbackOpen = async cb => {
    setIsOpen(false);
    cb();
  };

  const allEntriesDisabled =
    !configManager.config.roomEntryDialog?.roomName?.enabled &&
    !configManager.config.roomEntryDialog?.joinMetaverse?.enabled &&
    !configManager.config.roomEntryDialog?.enterOnAR?.enabled &&
    !configManager.config.roomEntryDialog?.enterOnVR?.enabled &&
    !configManager.config.roomEntryDialog?.lookAround?.enabled &&
    !configManager.config.roomEntryDialog?.roomSettings?.enabled &&
    !configManager.config.roomEntryDialog?.poweredBy?.enabled;

  if (allEntriesDisabled) {
    return <></>;
  }

  return (
    <OverlayDrawer
      id="room-entry-modal"
      position={["xs", "sm", "md"].includes(breakpoint) ? "bottom" : "end"}
      open={open}
      modalType="non-modal"
      className={mergeClasses(styles.entryCard)}
    >
      <DrawerHeader className={classes.titleContainer} action={<></>}>
        {configManager.config.roomEntryDialog.exitButton.enabled && (
          <Button id="room-exit-button" appearance="subtle" onClick={onBack} icon={<Dismiss20Regular />} className={classes.closeButton} />
        )}
        <DrawerHeaderTitle style={{ width: "100%" }}>
          {configManager.config.roomEntryDialog.roomName.enabled && <Subtitle1>{roomName}</Subtitle1>}
        </DrawerHeaderTitle>
      </DrawerHeader>
      <DrawerBody className={classes.dialogContent}>
        {isReady && canEnter ? (
          !timeLeft ? (
            <>
              {showJoinRoom && configManager.config.roomEntryDialog.joinMetaverse.enabled && (
                <Button
                  id="join-verse-button"
                  icon={<EnterIcon />}
                  className={classes.button}
                  appearance="primary"
                  onClick={() => callbackOpen(onJoinRoom)}
                >
                  <span>
                    <FormattedMessage id="room-entry-modal.join-room-button" defaultMessage="Join Verse" />
                  </span>
                </Button>
              )}
              {configManager.config.roomEntryDialog.enterOnAR.enabled && (
                <Button
                  id="enter-ar-button"
                  className={classes.button}
                  appearance="secondary"
                  icon={<EnterOnAR />}
                  onClick={() =>
                    callbackOpen(async () => {
                      onJoinAR();
                      await new Promise(r => setTimeout(r, 0));
                      onJoinRoom();
                    })
                  }
                >
                  <span>
                    <FormattedMessage id="room-entry-modal.enter-on-ar-button" defaultMessage="Enter on AR" />
                  </span>
                </Button>
              )}
              {showEnterOnDevice && configManager.config.roomEntryDialog?.enterOnVR.enabled && (
                <Button id="enter-vr-button" icon={<EnterOnVR />} className={classes.button} onClick={() => callbackOpen(onEnterOnDevice)}>
                  <span>
                    <FormattedMessage id="room-entry-modal.enter-on-device-button" defaultMessage="Enter on VR" />
                  </span>
                </Button>
              )}
            </>
          ) : (
            <CountdownDisplay timeLeft={timeLeft} styles={styles} />
          )
        ) : showSpectate && canEnter == false ? (
          <div className={styles.roomFull}>
            <Warning20Filled />
            <FormattedMessage
              id="room-entry-modal.room-full"
              defaultMessage="This verse has reached its maximum capacity for active participants."
            />
          </div>
        ) : (
          ""
        )}
        {showSpectate && configManager.config.roomEntryDialog?.lookAround?.enabled && (
          <Button id="look-around-button" icon={<ShowIcon />} className={classes.button} onClick={onSpectate}>
            <span>
              <FormattedMessage id="room-entry-modal.spectate-button" defaultMessage="Look around" />
            </span>
          </Button>
        )}
        {breakpoint !== "sm" && showVerseSettings && (
          <>
            <Button id="settings-button" icon={<Settings20Filled />} className={classes.button} onClick={onVerseSettings}>
              <span>
                <FormattedMessage id="room-entry-modal.room-settings-button" defaultMessage="Settings" />
              </span>
            </Button>
          </>
        )}
      </DrawerBody>
      {configManager.config.roomEntryDialog.poweredBy.enabled && (
        <DrawerFooter>
          <Text style={{ textAlign: "center", width: "100%" }} align="center">
            Powered by MUD XR Verse
          </Text>
        </DrawerFooter>
      )}
    </OverlayDrawer>
  );
}

RoomEntryModal.propTypes = {
  className: PropTypes.string,
  roomId: PropTypes.string,
  roomName: PropTypes.string.isRequired,
  roomDescription: PropTypes.string,
  showJoinRoom: PropTypes.bool,
  onJoinRoom: PropTypes.func,
  showEnterOnDevice: PropTypes.bool,
  onEnterOnDevice: PropTypes.func,
  showSpectate: PropTypes.bool,
  onSpectate: PropTypes.func,
  roomUserData: PropTypes.object,
  canEnter: PropTypes.bool,
  onJoinAR: PropTypes.func
};

RoomEntryModal.defaultProps = {
  showJoinRoom: true,
  showEnterOnDevice: true,
  showSpectate: true,
  roomUserData: {},
  canEnter: false
};
